import { useUpload } from "./UploadContext"; // Adjust path as needed
import { useNavigate } from "react-router-dom";
import Draggable, { DraggableEventHandler } from "react-draggable";
import { t } from "i18next";
import { useEffect, useState } from "react";

/**
 * A draggable popup component that displays the progress of an ongoing upload.
 * The popup can be dragged around within its parent container and will navigate
 * to the deployment form when clicked (or activated via Enter/Space, since it's
 * keyboard-focusable), provided it is not being dragged.
 *
 * @returns {JSX.Element | null} The draggable popup component or null if no upload is in progress.
 *
 * @remarks
 * This component uses the `react-draggable` library to enable dragging functionality.
 * It also utilizes the `useUpload` hook to get the upload progress and status.
 *
 */
const UploadProgressDraggablePopup = () => {
  const {
    uploadProgress,
    inProgress,
    uploadIsPaused,
    uploadIsStopped,
    projectGuid,
    deploymentGuid,
    deploymentName,
    deploymentId,
    selectedFiles,
    uploadedFileCount,
    skippedFileCount,
  } = useUpload();
  const navigate = useNavigate();
  const [isDragging, setIsDragging] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Bring the popup back the next time a (new or different) upload starts.
  useEffect(() => {
    if (inProgress) setDismissed(false);
  }, [deploymentId, inProgress]);

  /**
   * Handles the drag start event by setting `isDragging` to true.
   */
  const handleDragStart = () => setIsDragging(true);

  /**
   * Handles the drag stop event by setting `isDragging` to false after a slight delay.
   */
  const handleDragStop: DraggableEventHandler = () => {
    setTimeout(() => setIsDragging(false), 100); // Slight delay to avoid race conditions
  };

  /**
   * Handles the click event on the popup by navigating to the media management page.
   */
  const handleClicked = () => {
    if (!isDragging) {
      // Navigate to the media management page
      navigate(`/manage/${projectGuid}/${deploymentGuid}`);
    }
  };

  // Hide the window if there's no ongoing upload, or the user dismissed it.
  if (!inProgress || dismissed) return null;

  const remaining =
    selectedFiles.length - (uploadedFileCount + skippedFileCount);
  const progressPct = Math.round(uploadProgress) || 0;

  return (
    <Draggable
      handle=".draggable-handle"
      bounds="parent"
      onStart={handleDragStart}
      onStop={handleDragStop}
    >
      {/* The card is one real <button> for "open this upload", with dismiss as a sibling
          rather than a control nested inside it — nesting the two made the semantics
          ambiguous for assistive tech and risked double activation. Its children are all
          spans: a <button>'s content model is phrasing content, so divs would not be
          valid there. Dismiss is positioned over the header instead of sitting in the
          flow, which is what keeps it outside the card button. */}
      <div
        style={{ position: "fixed", bottom: "1.5rem", right: "1.5rem" }}
        className="relative z-[60] w-72 overflow-hidden rounded-xl border border-off-white-dark bg-white shadow-2xl transition duration-300 dark:border-storm-blue-border dark:bg-storm-blue-card"
      >
        <button
          type="button"
          onClick={handleClicked}
          aria-label={t("View upload progress for {{name}}", {
            name: deploymentName,
          })}
          className="block w-full cursor-pointer text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-storm-blue dark:focus-visible:ring-sky-blue"
        >
          {/* Padded right so the long deployment name truncates before it reaches the
              dismiss button overlaying this row. */}
          <span className="draggable-handle flex cursor-move items-center gap-2 border-b border-off-white-dark py-2.5 pl-3.5 pr-10 transition duration-300 dark:border-storm-blue-border">
            <span className="h-[7px] w-[7px] flex-none rounded-full bg-sky-blue" />
            <span className="truncate text-[12.5px] font-semibold text-primary-off-black transition duration-300 dark:text-primary-off-white">
              {deploymentName}
            </span>
          </span>
          <span className="block px-3.5 py-3">
            <span className="flex items-baseline justify-between">
              {/* Announced: while the user is on another page this popup is the only
                  thing that reports the upload stopping. Scoped to the status word —
                  the percentage and the remaining count beside it change on every file
                  and would be announced continuously. */}
              <span
                aria-live="polite"
                className="text-xs text-off-black-light transition duration-300 dark:text-greyed-text"
              >
                {uploadIsStopped
                  ? t("Upload stopped")
                  : uploadIsPaused
                    ? t("Paused")
                    : t("Uploading")}
              </span>
              <span className="text-[12.5px] font-semibold text-primary-off-black transition duration-300 dark:text-primary-off-white">
                {progressPct}%
              </span>
            </span>
            <span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-off-white-dark transition duration-300 dark:bg-storm-blue">
              <span
                className="block h-full rounded-full bg-gradient-to-r from-primary-blue-light to-sky-blue transition-all duration-300"
                style={{ width: `${progressPct}%` }}
              />
            </span>
            <span className="mt-2 block text-[11.5px] text-off-black-light transition duration-300 dark:text-greyed-text">
              {t("{{count}} files remaining", { count: remaining })}
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label={t("Dismiss")}
          className="absolute right-3.5 top-2.5 text-off-black-light transition duration-150 hover:text-primary-off-black focus:outline-none focus-visible:ring-2 focus-visible:ring-storm-blue dark:focus-visible:ring-sky-blue dark:text-greyed-text dark:hover:text-primary-off-white"
        >
          ✕
        </button>
      </div>
    </Draggable>
  );
};

export default UploadProgressDraggablePopup;
